import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";

import {
  KernelLockup,
  Loading,
  Permission,
  SchemeToggle,
  kernel,
  kernelPost,
  resume,
  signIn,
  signOut,
} from "@lopie/kernel-ui";

import { GROUPS, SECTIONS } from "./sections/index.jsx";

/**
 * Agent OS — who is acting inside this kernel.
 *
 * **It is an app on the kernel, not a page inside the console.** Its own
 * Cognito client, its own committed manifest, its own row on the Apps screen,
 * and a ceiling narrow enough to be embarrassing: one declared action and no
 * business type at all. A surface built for watching what agents are allowed
 * to do that was itself outside the model would be arguing against the thing
 * it ships with.
 *
 * The shell holds three things every Kernel app's shell holds, and holds them
 * once so no screen can forget: the brand, the session, and the strip that
 * says why you are seeing what you are seeing.
 */

const NAV_WIDTH = 240;

/**
 * Only reached when something went wrong or just after a sign-out.
 *
 * The happy path does not stop here: arriving at Agent OS *is* the request to
 * sign in, and asking somebody to confirm it with a click on an otherwise
 * empty page is asking them to do the thing they already did.
 */
function SignedOut({ error, deliberate }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", px: 3 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 440, alignItems: "flex-start" }}>
        <KernelLockup height={26} subtitle="Agent OS · powered by Lopie One" />
        {error ? (
          <Alert severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        ) : (
          <Typography color="text.secondary">
            {deliberate
              ? "Signed out. Nothing is cached here — every screen in Agent OS is read from the kernel under this app's own manifest, so there is nothing left in this page to show you."
              : "Taking you to sign in…"}
          </Typography>
        )}
        <Button variant="contained" onClick={signIn}>
          {error ? "Try again" : "Sign in"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(
    () => window.location.hash.replace("#", "") || SECTIONS[0].id,
  );

  // What the screen on show last resolved as its effective permission. Lifted
  // to the shell because "why am I seeing this" is a shell concern: making
  // every page remember to render it is how most of them forget.
  const [effective, setEffective] = useState(null);

  useEffect(() => {
    const deliberate = new URLSearchParams(window.location.search).has("signedout");
    resume()
      .then((found) => {
        setSession(found);
        if (!found && !deliberate) {
          signIn();
          return;
        }
        setReady(true);
      })
      .catch((e) => {
        setError(e.message);
        setReady(true);
      });
  }, []);

  useEffect(() => {
    window.location.hash = current;
    setEffective(null);
  }, [current]);

  // The hash was written and never read again after the first render, so the
  // back button did nothing and a link to `#attended` opened whichever screen
  // the app had been left on. Both are the same bug: the URL was an output of
  // this state and not an input to it.
  useEffect(() => {
    const follow = () => {
      const wanted = window.location.hash.replace("#", "");
      if (SECTIONS.some((s) => s.id === wanted)) setCurrent(wanted);
    };
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);

  const get = useMemo(() => (session ? kernel(session.token) : null), [session]);
  // Handed to every section for the same reason `get` is. Agent OS is the app
  // that *operates* agents — switching one off, setting when it runs, changing
  // what it was told — and every one of those is a POST. Without this the
  // controls still render and every one of them is disabled, which reads as
  // "you are not allowed to" rather than "this was never wired up".
  const post = useMemo(
    () => (session ? kernelPost(session.token) : null),
    [session],
  );
  const onEffective = useCallback((next) => setEffective(next), []);

  if (!ready) return <Loading label="Restoring your session" minHeight="100vh" />;

  if (!session) {
    return (
      <SignedOut
        error={error}
        deliberate={new URLSearchParams(window.location.search).has("signedout")}
      />
    );
  }

  const section = SECTIONS.find((s) => s.id === current) || SECTIONS[0];
  const Section = section.component;
  const groups = session.claims["cognito:groups"] || [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* The same two pixels the console carries, and for the same reason:
          it is the one place the accent appears without carrying a meaning.
          Two Kernel surfaces should be recognisably the same product before
          you have read a word on either. */}
      <Box sx={{ height: 2, bgcolor: "primary.main" }} />
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {/* Wraps at 390: an app bar carrying a wordmark, a name, a username
            chip and two buttons does not fit a phone on one line, and a
            toolbar that does not wrap pushes the whole page sideways. */}
        <Toolbar sx={{ gap: 1.5, flexWrap: "wrap", py: 1 }}>
          <KernelLockup height={22} subtitle="powered by Lopie One" />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Agent OS
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Chip
            size="small"
            variant="outlined"
            label={session.claims.username || session.claims.sub}
            sx={{ maxWidth: 220 }}
          />
          <SchemeToggle />
          <Button size="small" onClick={signOut}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: NAV_WIDTH,
              position: "static",
              // The questions under each label wrap, and without this the
              // wrapped text puts a horizontal scrollbar in a two-item list,
              // which reads as something being cut off.
              overflowX: "hidden",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            },
          }}
        >
          <List dense sx={{ px: 1, py: 2 }}>
            {GROUPS.map((group) => (
              <Box component="li" key={group.id} sx={{ listStyle: "none" }}>
                <ListSubheader
                  // `div`: this wrapper is already the <li>, and MUI renders
                  // a subheader as one by default, which is an <li> inside an
                  // <li>.
                  component="div"
                  disableGutters
                  disableSticky
                  title={group.blurb}
                  sx={{
                    bgcolor: "transparent",
                    color: "text.secondary",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                    px: 1.5,
                    pt: 2.5,
                    pb: 1,
                  }}
                >
                  {group.label}
                </ListSubheader>
                <List dense disablePadding>
                  {SECTIONS.filter((s) => s.group === group.id).map((s) => (
                  <ListItemButton
                    key={s.id}
                    selected={current === s.id}
                    onClick={() => setCurrent(s.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.25,
                      pl: 1.5,
                      borderLeft: 2,
                      borderColor:
                        current === s.id ? "primary.main" : "transparent",
                      transition: "border-color 140ms, background-color 140ms",
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                      },
                      "&.Mui-selected": { backgroundColor: "action.hover" },
                    }}
                  >
                    <ListItemText
                      primary={s.label}
                      secondary={s.question}
                      // `sx`, not bare props — these are `Typography` slots and
                      // v9 forwards no system props to them, so this nav had the
                      // same silent failure the console's did: `fontSize`
                      // dropped, `lineheight` written onto the DOM as an
                      // attribute, and every question 14px instead of 12.
                      slotProps={{
                        primary: {
                          sx: {
                            fontWeight: current === s.id ? 600 : 500,
                            color:
                              current === s.id ? "primary.main" : "text.primary",
                          },
                        },
                        secondary: {
                          sx: { fontSize: "0.75rem", lineHeight: 1.4 },
                        },
                      }}
                    />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </List>
        </Drawer>

        {/* Below md the drawer is hidden, so the sections become a wrapping
            row of buttons rather than disappearing. minWidth: 0 on the
            column is what stops a long monospace string inside a screen from
            widening the whole flex row. */}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: "flex", md: "none" }, flexWrap: "wrap" }}
            >
              {SECTIONS.map((s) => (
                <Button
                  key={s.id}
                  size="small"
                  variant={current === s.id ? "contained" : "outlined"}
                  onClick={() => setCurrent(s.id)}
                >
                  {s.label}
                </Button>
              ))}
            </Stack>

            <Box sx={{ pt: { xs: 0.5, md: 1 } }}>
              <Typography
                variant="overline"
                color="primary.main"
                sx={{ display: "block" }}
              >
                {section.label}
              </Typography>
              <Typography variant="h1" sx={{ maxWidth: "34ch" }}>
                {section.question}
              </Typography>
            </Box>

            {/* In the shell, once. `effective` stays null on both screens
                here and that is correct rather than missing: neither is
                governed by the tag intersection, so each passes `gate` and
                the strip says what does govern it instead of rendering an
                empty intersection, which reads as "permitted nothing". */}
            <Permission groups={groups} effective={effective} gate={section.gate} />

            <Divider />

            <Section get={get} post={post} onEffective={onEffective} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
